import { Link, useNavigate } from "react-router-dom";
import {
  TbBrandGithubFilled,
  TbBrandLinkedin,
  TbMail,
  TbBrandYoutube,
  TbUser,
  TbLogout,
  TbChevronDown,
} from "react-icons/tb";
import { useUser } from "../../../contexts/user";
import { useSetting } from "../../../contexts/settings";
import { useState } from "react";
import InlineHighlight from "../../content/InlineHighlight/InlineHighlight";
import { isExternalHref } from '../../../utils/internalHref';

const Header = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  // The fallback is the live value: the `Setting` table in prod is empty (checked
  // 12 Aug 2026), so nothing has ever overridden this and `useSetting` has always
  // returned the string written here.
  const tagline = useSetting("brandTagline", "Helping engineers take LLM and agent systems from demo to production");
  const socialLinks = [
    { href: useSetting("socialLinkedIn", "https://www.linkedin.com/in/gazar"), icon: TbBrandLinkedin, label: "LinkedIn" },
    { href: useSetting("socialYouTube", "https://www.youtube.com/@gazarbreakpoint"), icon: TbBrandYoutube, label: "YouTube" },
    { href: useSetting("socialGitHub", "https://github.com/ehsangazar"), icon: TbBrandGithubFilled, label: "GitHub" },
    { href: `mailto:${useSetting("contactEmail", "me@gazar.dev")}`, icon: TbMail, label: "Email" },
  ];
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const displayName = user?.name || user?.email || "Account";

  return (
    <header className="bg-brand-cream border-b border-border-subtle relative z-[100]">
      <div className="w-full">
        <div className="h-1 w-full" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between py-5 md:py-6 lg:py-7 gap-4 md:gap-6">
            {/* Brand — wordmark only. The portrait lives once, prominently, in
                the homepage hero; a small header avatar duplicated it in the
                first viewport and read as a "logo". A clean wordmark is stronger
                brand chrome and is consistent across every page. */}
            <Link
              to="/"
              className="flex items-center flex-1 min-w-0 group"
            >
              <div className="flex flex-col min-w-0 flex-1 gap-1">
                <span
                  className="font-heading text-brand-navy text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.5rem] font-black uppercase leading-[1.1] truncate m-0 group-hover:opacity-80 transition-opacity duration-150 block"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  GAZAR
                </span>

                <p
                  className="font-body text-ink-soft text-sm sm:text-base md:text-lg leading-[1.4] font-normal m-0 hidden sm:block"
                  style={{ letterSpacing: "-0.003em" }}
                >
                  <InlineHighlight color="navy">
                    {tagline}
                  </InlineHighlight>
                </p>
              </div>
            </Link>

            {/* Right: Social Links & Auth */}
            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              {/* Social Links — Desktop */}
              <div className="hidden lg:flex items-center gap-2">
                {socialLinks.map((link) => {
                  const IconComponent = link.icon;
                  const isExternal = isExternalHref(link.href);
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="group relative"
                      title={link.label}
                    >
                      <span className="inline-flex items-center justify-center w-12 h-12 rounded-smooth bg-brand-cream text-brand-navy border border-border-subtle transition-colors duration-150 group-hover:bg-brand-navy group-hover:text-white group-hover:border-brand-navy">
                        <IconComponent size={18} />
                      </span>
                    </a>
                  );
                })}
              </div>

              {/* Auth */}
              {user ? (
                <div className="relative z-[101]">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2.5 px-4 py-2.5 lg:px-5 lg:py-3 bg-brand-cream hover:bg-white text-brand-navy rounded-smooth transition-colors duration-150 border border-border-subtle hover:border-border-strong font-heading text-base lg:text-lg font-semibold leading-[1.35] min-h-[44px]"
                    aria-label="User menu"
                    aria-expanded={isUserMenuOpen}
                  >
                    <TbUser size={20} />
                    <span className="hidden sm:inline font-medium">{displayName}</span>
                    <TbChevronDown
                      size={18}
                      className={`transition-transform duration-150 ${isUserMenuOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[9998]"
                        onClick={() => setIsUserMenuOpen(false)}
                        aria-hidden="true"
                      />

                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-smooth-lg border border-border-strong py-2 z-[9999] overflow-hidden divide-y divide-border-subtle">
                        <Link
                          to="/account"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-3 px-5 py-3.5 text-brand-navy hover:bg-brand-cream transition-colors duration-150 font-heading text-base leading-[1.35] font-medium min-h-[44px]"
                        >
                          <TbUser size={20} className="text-brand-navy/60" />
                          <span className="font-semibold">My account</span>
                        </Link>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 text-brand-navy hover:bg-brand-cream transition-colors duration-150 font-heading text-base leading-[1.35] font-medium min-h-[44px]"
                        >
                          <TbLogout size={20} />
                          <span className="font-semibold">Sign out</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-4 py-2.5 lg:px-5 lg:py-3 bg-brand-navy hover:opacity-90 text-white rounded-smooth transition-opacity duration-150 font-heading text-base lg:text-lg font-semibold leading-[1.35] min-h-[44px]"
                >
                  <TbUser size={20} />
                  <span className="hidden sm:inline">Sign in</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
